import { supabase } from '../../../shared/lib/supabase';

/** Predefined app names for organisation_app_access (value = DB value, label = display) */
export const APP_ACCESS_APP_NAMES: { value: string; label: string }[] = [
  { value: 'replay', label: 'Replay' },
  { value: 'capture', label: 'Capture' },
  { value: 'score', label: 'Score' },
  { value: 'cms', label: 'CMS' },
  { value: 'roster', label: 'Roster' },
  { value: 'reports', label: 'Reports' },
  { value: 'control-room', label: 'Control Room' },
];

// Define the AppAccess interface based on your sample data
export interface AppAccess {
  id: string;
  organisation_id: string;
  app_name: string;
  access_level: string;
  granted_by: string | null;
  granted_at: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  organisation_name?: string;
}

export interface AppAccessCreateInput {
  organisation_id: string;
  app_name: string;
  access_level: string;
  granted_by?: string | null;
  granted_at: string;
  is_active: boolean;
}

export interface AppAccessUpdateInput {
  organisation_id?: string;
  app_name?: string;
  access_level?: string;
  granted_by?: string | null;
  granted_at?: string;
  is_active?: boolean;
}

class AppAccessService {
  async getAll(): Promise<AppAccess[]> {
    try {
      console.log('🔍 Fetching app access with joins...');

      const { data, error } = await supabase
        .from('organisation_app_access')
        .select(`
          *,
          organisations!organisation_app_access_organisation_id_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching app access:', error);
        throw error;
      }

      // Transform the data to include joined fields
      const transformedData = data?.map(item => ({
        ...item,
        organisation_name: item.organisations?.name || ''
      })) || [];

      console.log('✅ App access fetched successfully:', transformedData.length, 'records');
      return transformedData;
    } catch (error) {
      console.error('❌ App access fetch error:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<AppAccess | null> {
    try {
      console.log(`🔍 Fetching app access with ID: ${id}`);

      const { data, error } = await supabase
        .from('organisation_app_access')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('❌ Error fetching app access by ID:', error);
        throw error;
      }

      console.log('✅ App access fetched successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ App access fetch by ID error:', error);
      throw error;
    }
  }

  async create(input: AppAccessCreateInput): Promise<AppAccess> {
    try {
      console.log('📝 Creating app access:', input);

      const { data: result, error } = await supabase
        .from('organisation_app_access')
        .insert(input)
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating app access:', error);
        throw error;
      }

      console.log('✅ App access created successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ App access creation error:', error);
      throw error;
    }
  }

  async update(id: string, data: AppAccessUpdateInput): Promise<AppAccess> {
    try {
      console.log(`📝 Updating app access ${id}:`, data);
      console.log('🔍 Data being sent:', JSON.stringify(data, null, 2));

      // First, let's check if the record exists
      const { data: existingRecord, error: fetchError } = await supabase
        .from('organisation_app_access')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        console.error('❌ Error fetching existing record:', fetchError);
        throw new Error(`Record not found: ${fetchError.message}`);
      }

      console.log('✅ Found existing record:', existingRecord);

      const { data: result, error } = await supabase
        .from('organisation_app_access')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select();

      if (error) {
        console.error('❌ Error updating app access:', error);
        throw error;
      }

      console.log('✅ App access updated successfully:', result?.[0] || result);
      return (Array.isArray(result) ? result[0] : result) as AppAccess;
    } catch (error) {
      console.error('❌ App access update error:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting app access: ${id}`);

      const { error } = await supabase
        .from('organisation_app_access')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ Error deleting app access:', error);
        throw error;
      }

      console.log('✅ App access deleted successfully');
    } catch (error) {
      console.error('❌ App access deletion error:', error);
      throw error;
    }
  }
}

export const appAccessService = new AppAccessService();
